import { RotateCcw } from 'lucide-react'
import { canResetToAI, type FieldSource } from '@ispot/shared'

interface FieldSourceBadgeProps {
  source: FieldSource
  aiValue: number | null
  currentValue: number | null
  onReset?: () => void
  showResetButton?: boolean
  compact?: boolean
}

const LABELS: Record<FieldSource, string> = {
  ai: 'AI',
  manual: 'Manual',
  edited: 'Edited',
}

const COLORS: Record<FieldSource, { bg: string; dot: string; text: string }> = {
  ai: { bg: '#e8f5f0', dot: 'var(--color-market)', text: '#00a08a' },
  manual: { bg: 'var(--color-warm-200)', dot: 'var(--color-text-muted)', text: 'var(--color-text-secondary)' },
  edited: { bg: '#f5ecd9', dot: '#c89a3c', text: '#a17a1f' },
}

export function FieldSourceBadge({
  source,
  aiValue,
  currentValue,
  onReset,
  showResetButton = true,
  compact = false,
}: FieldSourceBadgeProps) {
  const c = COLORS[source]
  const canReset =
    showResetButton &&
    onReset &&
    canResetToAI({ value: currentValue, source, aiValue } as { value: number | null; source: FieldSource; aiValue: number | null })

  return (
    <div className="flex items-center gap-2">
      <div
        className="inline-flex items-center gap-1.5 rounded-full"
        style={{
          backgroundColor: c.bg,
          padding: compact ? '2px 7px' : '3px 8px',
        }}
      >
        <span
          className="inline-block rounded-full"
          style={{ width: 6, height: 6, backgroundColor: c.dot }}
        />
        <span
          className="font-semibold"
          style={{ color: c.text, fontSize: 10, letterSpacing: 0.3 }}
        >
          {LABELS[source]}
        </span>
      </div>
      {canReset ? (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] transition-colors hover:bg-[var(--color-warm-100)]"
          style={{
            borderColor: 'var(--color-border-subtle)',
            color: 'var(--color-text-secondary)',
          }}
          title={`Reset to AI value: ${formatAiHint(aiValue)}`}
        >
          <RotateCcw className="h-2.5 w-2.5" />
          Reset
        </button>
      ) : null}
    </div>
  )
}

function formatAiHint(v: number | null): string {
  if (v === null) return '—'
  if (Math.abs(v) >= 1e9) return `${(v / 1e9).toFixed(1)}B`
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}k`
  return String(v)
}
